import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import { Button, DialogActions, IconButton } from "@mui/material";
import { FieldArray, useFormikContext } from "formik";
import React from "react";

import { ResponseScriptKeyword } from "~frontend/__generated__/graphql";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { ScriptEditor } from "~frontend/routes/subaccounts/campaignEditor/Script/ScriptEditor";
import { ScriptFormEditorWithPreview } from "~frontend/routes/subaccounts/campaignEditor/Script/ScriptVariants";
import ShaftWarnings from "~frontend/routes/subaccounts/campaignEditor/Script/ShaftWarnings";
import { CampaignForEditor } from "~frontend/routes/subaccounts/campaignEditor/types";

import { FormikTextField } from "../forms/FormTextField";
import FormikErrorBanner from "../forms/FormikErrorBanner";
import { MediaFieldValue } from "../forms/FormikMediaField";
import {
  AutocompleteValue,
  FormikInMemoryCombobox,
} from "../forms/InMemoryCombobox";
import { FormikTagCombobox } from "../forms/TagCombobox";
import {
  KeywordContainer,
  KeywordHelp,
  ScriptFormDialogButtons,
  ScriptKeywordBody,
  ScriptKeywordDetails,
  ScriptKeywordsTitle,
} from "./styles";

export function ResponseScriptEditForm({
  onClose,
  tags,
  groups,
  keywords,
  submitText,
  loadingText,
  campaign,
}: {
  onClose: () => void;
  tags: AutocompleteValue[];
  groups: AutocompleteValue[];
  keywords: ResponseScriptKeyword[];
  submitText: string;
  loadingText: string;
  campaign?: CampaignForEditor;
}): React.ReactElement {
  const form = useFormikContext<{
    script: string;
    media: unknown;
    keywordIdsToDelete?: string[];
    keywordsToAdd: string[];
  }>();

  return (
    <>
      <FormikErrorBanner />
      <ScriptFormEditorWithPreview>
        <FormikTextField
          name="title"
          label="Title"
          fullWidth
          inputProps={{
            "data-testid": "add-script-response-title-field",
          }}
        />
        <ScriptEditor
          type="response"
          script={form.values.script}
          media={form.values.media as MediaFieldValue}
          campaign={campaign}
        />
        <ShaftWarnings renderedMessage={form.values.script} />
      </ScriptFormEditorWithPreview>
      <FormikTagCombobox
        name={"tagIds"}
        label={"Apply Tags"}
        bottomLabel={"Multiple tags can be applied to each Response Script"}
        options={tags}
      />
      <FormikInMemoryCombobox
        name={"groupIds"}
        label="Add to a Response Group"
        options={groups}
        bottomLabel={"Response Scripts can be added to multiple groups"}
      />

      {keywords.length || form.values.keywordsToAdd.length ? (
        <>
          <KeywordHelp>
            A keyword is a word or phrase that will automatically trigger this
            response script if a contact sends it. The keyword will only trigger
            a response if it is sent to a campaign that has this response
            script.
          </KeywordHelp>

          {form.values.keywordIdsToDelete ? (
            <FieldArray
              name="keywordIdsToDelete"
              render={({ insert, remove }) => (
                <>
                  {keywords.map(({ id, normalizedKeyword }) => (
                    <KeywordContainer key={`keyword-container-${id}`}>
                      <ScriptKeywordDetails>
                        <ScriptKeywordsTitle>
                          Keyword:&nbsp;
                        </ScriptKeywordsTitle>
                        <ScriptKeywordBody
                          sx={{
                            textDecoration:
                              form.values.keywordIdsToDelete!.includes(id)
                                ? "line-through"
                                : null,
                          }}
                        >
                          {`“${normalizedKeyword.toUpperCase()}”`}
                        </ScriptKeywordBody>
                      </ScriptKeywordDetails>

                      {!form.values.keywordIdsToDelete!.includes(id) ? (
                        <IconButton
                          onClick={() => {
                            if (!form.values.keywordIdsToDelete!.includes(id)) {
                              insert(
                                form.values.keywordIdsToDelete!.length,
                                id
                              );
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => {
                            const index =
                              form.values.keywordIdsToDelete!.indexOf(id);
                            if (index > -1) {
                              remove(index);
                            }
                          }}
                        >
                          <UndoIcon />
                        </IconButton>
                      )}
                    </KeywordContainer>
                  ))}
                </>
              )}
            />
          ) : null}

          <FieldArray
            name="keywordsToAdd"
            render={({ remove }) => (
              <>
                {form.values.keywordsToAdd.map((_, index) => (
                  <KeywordContainer>
                    <FormikTextField
                      name={`keywordsToAdd.${index}`}
                      label="Keyword (optional)"
                      fullWidth
                    />

                    <IconButton
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </KeywordContainer>
                ))}
              </>
            )}
          />
        </>
      ) : null}

      <DialogActions
        sx={{
          "flex-start": undefined,
          justifyContent: "space-between",
        }}
      >
        <FieldArray
          name="keywordsToAdd"
          render={({ insert }) => (
            <Button
              variant="contained"
              color="tertiary"
              onClick={() => insert(form.values.keywordsToAdd.length, "")}
            >
              + Add a Keyword
            </Button>
          )}
        />

        <ScriptFormDialogButtons>
          <Button
            disabled={form.isSubmitting}
            onClick={onClose}
            color="secondary"
          >
            Cancel
          </Button>

          <FormikSubmitButton
            color="primary"
            variant="contained"
            submitText={submitText}
            loadingText={loadingText}
          />
        </ScriptFormDialogButtons>
      </DialogActions>
    </>
  );
}
